import {
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  makeStyles,
  Paper
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import { MenuErrorFragment } from "@saleor/fragments/types/MenuErrorFragment";
import { buttonMessages } from "@saleor/intl";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

export interface AppConfigUploadDialogFormData {
  name: string;
  json: File;
}

export interface AppConfigUploadDialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  onFileError: () => void;
  disabled: boolean;
  params: string;
  errors: MenuErrorFragment[];
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSave: (data: AppConfigUploadDialogFormData) => void;
}

const useStyles = makeStyles(
  {
    descriptionLabel: {
      margin: "5px 0px 5px 0px"
    },
    fileUploadField: {
      height: "175px",
      paddingTop: "20px",
      textAlign: "center"
    },
    hide: {
      display: "none"
    },
    root: {
      display: "flex"
    }
  },
  { name: "AppConfigUploadDialog" }
);

const AppConfigUploadDialog: React.FC<AppConfigUploadDialogProps> = props => {
  const {
    disabled,
    onFileError,
    params,
    onClose,
    onSave,
    open,
    loading
  } = props;

  const initialForm: AppConfigUploadDialogFormData = {
    json: null,
    name: ""
  };

  const intl = useIntl();
  const classes = useStyles(props);
  const [getFile, setFile] = React.useState(null);
  const upload = React.useRef(null);
  const [error, setError] = React.useState("");

  const checkFile = e => {
    if (e[0].type !== "application/json") {
      onFileError();
    } else {
      setFile(e[0]);
    }
  };

  const handleSubmit = event => {
    if (event?.name.match(/^[a-zA-Z0-9]*$/)) {
      setError("");
      onSave(event);
    } else {
      setError("Please input Alphanumeric characters only");
      setInterval(() => {
        setError("");
      }, 3000);
    }
  };

  const handleClose = () => {
    onClose();
    setFile(null);
    setError("");
  };

  React.useEffect(() => {
    if (!open) {
      setFile(null);
    }
  }, [open]);

  return (
    <Dialog onClose={handleClose} maxWidth="sm" fullWidth open={open}>
      <DialogTitle>
        <FormattedMessage
          defaultMessage="Upload file"
          description="dialog header"
          id="appConfigUploadDialogHeader"
        />
      </DialogTitle>
      <Form initial={initialForm} onSubmit={handleSubmit}>
        {({ change, data, submit }) => {
          React.useEffect(() => {
            if (getFile) {
              data.json = getFile;
            } else {
              data.json = null;
            }
          }, [getFile]);

          return (
            <>
              <DialogContent>
                <TextField
                  className={params === "update" ? classes.hide : null}
                  disabled={disabled || params === "update"}
                  id="appNameTxt"
                  error={!!error}
                  helperText={error}
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "App Name",
                    id: "appUploadDialogTitleLabel"
                  })}
                  name={"name" as keyof AppConfigUploadDialogFormData}
                  value={params === "update" ? (data.name = "none") : data.name}
                  onChange={change}
                />
                {getFile ? (
                  <Paper>
                    <List>
                      <ListItem>
                        {getFile.name}
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => setFile(null)}
                          >
                            {loading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                  </Paper>
                ) : (
                  <Dropzone onDrop={e => checkFile(e)}>
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()}>
                        <Paper
                          className={classes.fileUploadField}
                          onClick={() => upload.current.click()}
                        >
                          <input
                            {...getInputProps()}
                            id="fileUpload"
                            accept=".json"
                            onChange={e => checkFile(e.target.files)}
                            ref={upload}
                            name={"json" as keyof AppConfigUploadDialogFormData}
                            type="file"
                          />
                          <CloudUploadIcon fontSize="large" />
                          <p className={classes.descriptionLabel}>
                            <FormattedMessage
                              defaultMessage="Drag and Drop Files"
                              description="description"
                            />
                          </p>
                          <p className={classes.descriptionLabel}>
                            <FormattedMessage
                              defaultMessage="Or"
                              description="description"
                            />
                          </p>
                          <Button color="primary" variant="contained">
                            <FormattedMessage
                              defaultMessage="Upload a file"
                              description="description"
                            />
                          </Button>
                        </Paper>
                      </div>
                    )}
                  </Dropzone>
                )}
              </DialogContent>
              {getFile && !loading && (
                <DialogActions>
                  <Button onClick={handleClose}>
                    <FormattedMessage {...buttonMessages.back} />
                  </Button>
                  <ConfirmButton
                    transitionState={`default`}
                    color="primary"
                    variant="contained"
                    onClick={submit}
                  >
                    <FormattedMessage {...buttonMessages.save} />
                  </ConfirmButton>
                </DialogActions>
              )}
            </>
          );
        }}
      </Form>
    </Dialog>
  );
};

AppConfigUploadDialog.displayName = "AppConfigUploadDialog";
export default AppConfigUploadDialog;
